"use client";
/**
 * Battle "juice" — the feel layer that sits on top of the pure battle logic.
 *
 * This module owns three things and nothing else:
 *
 *  1. A tiny synchronous event bus (`onJuice` / `emitJuice`). The
 *     battle-store-bridge emits high-level feedback events (hit, crit, KO…);
 *     <BattleEffects> subscribes and turns them into screen shake, camera
 *     kick/zoom, hit-stop and a crit flash. We use a bus (not the game store)
 *     so this track stays inside its file-ownership boundary — no store edits.
 *
 *  2. Haptics via `navigator.vibrate`, feature-detected and settings-gated.
 *
 *  3. A persisted juice toggle + a live `prefers-reduced-motion` reading, so
 *     both the bridge and the effects layer can ask one question:
 *     `juiceEnabled()` → should we play motion/haptics at all?
 *
 * SSR-safe: every export tolerates `typeof window === 'undefined'`.
 */

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** Severity of a feedback beat, used to scale shake / haptic strength. */
export type JuiceIntensity = 'light' | 'normal' | 'heavy';

export type JuiceEvent =
  | {
      /** Something got hit. */
      kind: 'hit';
      target: 'player' | 'opponent';
      /** Raw damage, used to scale screen shake. */
      amount: number;
      /** Type effectiveness multiplier (1 = neutral, >1 super, <1 weak). */
      effectiveness: number;
      /** Whether this hit was a critical. */
      crit: boolean;
    }
  | { kind: 'crit'; target: 'player' | 'opponent' }
  | { kind: 'ko'; target: 'player' | 'opponent' }
  | { kind: 'catch' }
  | { kind: 'victory' }
  | { kind: 'defeat' };

type JuiceListener = (e: JuiceEvent) => void;

const listeners = new Set<JuiceListener>();

/**
 * Subscribe to juice events. Returns an unsubscribe function (call it from a
 * React effect cleanup). No-op on SSR.
 */
export function onJuice(fn: JuiceListener): () => void {
  if (typeof window === 'undefined') return () => {};
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Fan a juice event out to all subscribers. Swallows listener errors. */
export function emitJuice(e: JuiceEvent): void {
  if (typeof window === 'undefined') return;
  for (const fn of listeners) {
    try {
      fn(e);
    } catch {
      /* ignore consumer errors — one bad listener must not stall a turn */
    }
  }
}

// ---------------------------------------------------------------------------
// Settings: juice toggle + reduced-motion
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'surggies:juice';

interface StoredJuice {
  enabled?: unknown;
  haptics?: unknown;
}

let _enabled = true;
let _haptics = true;
let _loaded = false;

function loadOnce(): void {
  if (_loaded || typeof window === 'undefined') return;
  _loaded = true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    const obj = parsed as StoredJuice;
    if (typeof obj.enabled === 'boolean') _enabled = obj.enabled;
    if (typeof obj.haptics === 'boolean') _haptics = obj.haptics;
  } catch {
    /* storage may be unavailable (private mode) — keep defaults */
  }
}

function save(): void {
  if (typeof window === 'undefined') return;
  try {
    const payload: StoredJuice = { enabled: _enabled, haptics: _haptics };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

/** True if the OS-level "reduce motion" preference is set. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * The single gate for all motion juice (shake, camera, hit-stop, flash).
 * Off when the player disabled juice OR the OS asks for reduced motion.
 */
export function juiceEnabled(): boolean {
  loadOnce();
  return _enabled && !prefersReducedMotion();
}

/** The gate for haptics specifically (independent toggle, same reduced-motion respect). */
export function hapticsEnabled(): boolean {
  loadOnce();
  return _haptics && !prefersReducedMotion();
}

/** Player-facing setter for the juice toggle (persists). */
export function setJuiceEnabled(on: boolean): void {
  loadOnce();
  _enabled = on;
  save();
}

/** Player-facing setter for the haptics toggle (persists). */
export function setHapticsEnabled(on: boolean): void {
  loadOnce();
  _haptics = on;
  save();
}

// ---------------------------------------------------------------------------
// Camera impulses: shake + kick/zoom
// ---------------------------------------------------------------------------
//
// The sprite layer lives in <BattleArena>, not in <BattleEffects>, so the
// transform that visibly "shakes the screen" must be applied there. To keep
// all the feel math in this one file, we expose `getCameraTransform(now)`:
// BattleArena samples it each frame and slaps the result onto its 16:9 panel.
// Impulses are time-decaying so multiple hits stack and settle on their own.

interface ShakeImpulse {
  start: number;
  duration: number;
  /** Peak displacement in px. */
  magnitude: number;
}

interface ZoomImpulse {
  start: number;
  duration: number;
  /** Peak extra zoom (e.g. 0.06 = +6%) and/or vertical kick in px. */
  zoom: number;
  kickY: number;
}

let shakes: ShakeImpulse[] = [];
let zooms: ZoomImpulse[] = [];

// Hit-stop: a wall-clock (performance.now) timestamp until which animation
// should hold. Set by the bridge on impact; read by <BattleEffects> (particle
// RAF) and, optionally, <BattleArena> (sprite layer). A freeze, not a stall —
// turn logic in the engine has already run; only the visuals pause.
let hitStopUntil = 0;

/**
 * Request a hit-stop freeze of `ms` from now (capped). Crits/super-effective
 * pass a longer value. No-op when juice is disabled.
 */
export function requestHitStop(ms: number): void {
  if (typeof performance === 'undefined' || !juiceEnabled()) return;
  const until = performance.now() + Math.min(260, Math.max(0, ms));
  if (until > hitStopUntil) hitStopUntil = until;
}

/** Remaining hit-stop in ms at time `now`, or 0 if not frozen. */
export function hitStopRemaining(now: number): number {
  const r = hitStopUntil - now;
  return r > 0 ? r : 0;
}

/** Map damage → shake magnitude (px), clamped. Bigger hits hit harder. */
function shakeMagnitudeFor(amount: number, effectiveness: number, crit: boolean): number {
  let m = 2 + Math.min(10, amount * 0.18); // ~2px floor, ~12px ceiling
  if (effectiveness > 1) m *= 1.35;
  if (crit) m *= 1.5;
  return Math.min(16, m);
}

/**
 * Queue a screen shake. Magnitude is derived from damage/effectiveness/crit.
 * No-op when juice is disabled or reduced-motion is requested.
 */
export function shakeFromHit(amount: number, effectiveness: number, crit: boolean): void {
  if (typeof performance === 'undefined' || !juiceEnabled()) return;
  const magnitude = shakeMagnitudeFor(amount, effectiveness, crit);
  const duration = crit ? 420 : effectiveness > 1 ? 340 : 220;
  shakes.push({ start: performance.now(), duration, magnitude });
  if (shakes.length > 6) shakes = shakes.slice(-6);
}

/** Queue a camera kick + zoom punch (super-effective hits, KOs). */
export function cameraPunch(opts: { zoom?: number; kickY?: number; duration?: number } = {}): void {
  if (typeof performance === 'undefined' || !juiceEnabled()) return;
  zooms.push({
    start: performance.now(),
    duration: opts.duration ?? 360,
    zoom: opts.zoom ?? 0.05,
    kickY: opts.kickY ?? 6,
  });
  if (zooms.length > 4) zooms = zooms.slice(-4);
}

/** True while any camera impulse is still animating — lets consumers skip work. */
export function cameraActive(now: number): boolean {
  for (const s of shakes) if (now - s.start < s.duration) return true;
  for (const z of zooms) if (now - z.start < z.duration) return true;
  return false;
}

/**
 * Sample the combined camera transform at time `now` (ms, performance.now()).
 * Returns a CSS `transform` string. `'none'` when idle. Safe to call every
 * frame; it also garbage-collects expired impulses.
 */
export function getCameraTransform(now: number): string {
  let dx = 0;
  let dy = 0;
  let scale = 1;

  const liveShakes: ShakeImpulse[] = [];
  for (const s of shakes) {
    const t = now - s.start;
    if (t >= s.duration) continue;
    liveShakes.push(s);
    const decay = 1 - t / s.duration; // linear settle
    const mag = s.magnitude * decay;
    // High-frequency oscillation with a pseudo-random phase per impulse.
    const phase = s.start; // stable per impulse
    dx += Math.sin((t / 1000) * 90 + phase) * mag;
    dy += Math.cos((t / 1000) * 78 + phase) * mag * 0.6;
  }
  shakes = liveShakes;

  const liveZooms: ZoomImpulse[] = [];
  for (const z of zooms) {
    const t = now - z.start;
    if (t >= z.duration) continue;
    liveZooms.push(z);
    // Ease out: fast punch in, slow settle (1 - (t/d)^2-ish via sin).
    const env = Math.sin((1 - t / z.duration) * Math.PI); // 0→1→0
    scale += z.zoom * env;
    dy -= z.kickY * env;
  }
  zooms = liveZooms;

  if (dx === 0 && dy === 0 && scale === 1) return 'none';
  return `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) scale(${scale.toFixed(4)})`;
}

// ---------------------------------------------------------------------------
// Haptics
// ---------------------------------------------------------------------------

type Vibrator = { vibrate?: (pattern: number | number[]) => boolean };

/**
 * Fire a short haptic pulse, feature-detected and settings-gated.
 * Patterns are intentionally tiny (10–50ms) so they read as "punch", not "buzz".
 */
export function haptic(kind: 'hit' | 'crit' | 'catch' | 'ko'): void {
  if (typeof navigator === 'undefined') return;
  if (!hapticsEnabled()) return;
  const nav = navigator as unknown as Vibrator;
  if (typeof nav.vibrate !== 'function') return;

  // Short pulses (ms). Crit/KO use a two-tap pattern for extra punch.
  const PATTERNS: Record<typeof kind, number | number[]> = {
    hit: 12,
    crit: [22, 30, 22],
    catch: [10, 40, 30],
    ko: [40, 60, 40],
  };
  try {
    nav.vibrate(PATTERNS[kind]);
  } catch {
    /* ignore — vibrate can throw if called without a user gesture */
  }
}
